import { ButtonGroup, IconButton } from '@cad-arc';
import { v4 } from 'uuid';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { useRelatedData } from '../../../related-data/use-related-data';
import { ConsolidatedLocationView, EventPersonDto, EventVehicleDto } from '@mark43/cad-api';
import { TabSection } from '../../../panels/entity-panels/tabs/tab-section';
import { useIntegratedActionBroadcaster } from '../../../action-broadcaster/use-integrated-action-broadcaster';
import {
    CadEventsTable,
    RmsHistoryTable,
    VehiclesTable,
} from './related-data-entity-tables-deprecated';
import { RelatedDataEntityRows } from './related-data-entity-rows';
import { CadEventRow } from './cad-event-row';
import { RmsHistoryOrVehicleRow } from './rms-history-or-vehicle-row';
import { useApplicationType } from '../../../common/hooks/use-application-type';

const MAX_CAD_EVENTS_TO_SHOW = 5;

export function RelatedDataTabCommonContent(props: {
    phoneNumbers: string[];
    reportingParties: EventPersonDto[];
    vehicles: EventVehicleDto[];
    location?: ConsolidatedLocationView;
    onViewMoreCadEvents: () => void;
    onViewMoreRmsReports: () => void;
    onViewMoreVehicles: () => void;
}) {
    const {
        enhancedRelatedData,
        disableLegacyMapAndSearches,
        searchWindowsEnabled,
    } = useEnabledFeatures();
    const { trigger } = useIntegratedActionBroadcaster();
    const {
        cadEvents,
        cadEventsCount,
        hasMoreCadEvents,
        hasMoreRmsReports,
        hasMoreVehicles,
        isLoadingPoll,
        isLoadingViewMore,
        rmsReports,
        rmsReportsCount,
        vehicles,
        vehiclesCount,
        isCadEventsPollEnabled,
        isRmsReportsPollEnabled,
        isVehiclesPollEnabled,
    } = useRelatedData();
    const { isFirstResponder } = useApplicationType();

    const rootWindowActionType = isFirstResponder ? 'CAD_SEARCH' : 'OPEN_EVENT_SEARCH_V2_WINDOW';

    const searchEventsByReportingParty = () => {
        trigger([
            {
                id: v4(),
                targetWindowType: 'ROOT',
                type: rootWindowActionType,
            },
            {
                id: v4(),
                type: 'SEARCH_EVENTS_BY_REPORTING_PARTIES',
                targetWindowType: 'EVENT_SEARCH_V2',
                payload: { reportingParties: props.reportingParties },
            },
        ]);
    };
    const searchEventsByPhoneNumbers = () => {
        trigger([
            {
                id: v4(),
                targetWindowType: 'ROOT',
                type: rootWindowActionType,
            },
            {
                id: v4(),
                type: 'SEARCH_EVENTS_BY_PHONE_NUMBERS',
                targetWindowType: 'EVENT_SEARCH_V2',
                payload: { phoneNumbers: props.phoneNumbers },
            },
        ]);
    };
    const searchEventsByLocation = () => {
        trigger([
            {
                id: v4(),
                targetWindowType: 'ROOT',
                type: rootWindowActionType,
            },
            {
                id: v4(),
                type: 'SEARCH_EVENTS_BY_LOCATION',
                targetWindowType: 'EVENT_SEARCH_V2',
                payload: { location: props.location },
            },
        ]);
    };
    const searchEventsByVehicles = () => {
        trigger([
            {
                id: v4(),
                targetWindowType: 'ROOT',
                type: rootWindowActionType,
            },
            {
                id: v4(),
                type: 'SEARCH_EVENTS_BY_VEHICLES',
                targetWindowType: 'EVENT_SEARCH_V2',
                payload: { vehicles: props.vehicles },
            },
        ]);
    };

    const disableSearchButtons =
        (!disableLegacyMapAndSearches && !searchWindowsEnabled) ||
        cadEventsCount < MAX_CAD_EVENTS_TO_SHOW;

    return (
        <>
            <TabSection
                titleRightContent={
                    <ButtonGroup size="sm">
                        <IconButton
                            onClick={searchEventsByReportingParty}
                            size="sm"
                            aria-label="Person"
                            icon="Person"
                            disabled={disableSearchButtons || props.reportingParties.length === 0}
                            data-testid="search-by-reporting-party-btn"
                        />
                        <IconButton
                            onClick={searchEventsByPhoneNumbers}
                            size="sm"
                            aria-label="Phone"
                            icon="Phone"
                            disabled={disableSearchButtons || props.phoneNumbers.length === 0}
                            data-testid="search-by-phone-numbers-btn"
                        />
                        <IconButton
                            onClick={searchEventsByLocation}
                            size="sm"
                            aria-label="Location"
                            icon="Location"
                            disabled={disableSearchButtons}
                            data-testid="search-by-location-btn"
                        />
                        <IconButton
                            onClick={searchEventsByVehicles}
                            size="sm"
                            aria-label="Vehicle"
                            icon="Vehicle"
                            disabled={disableSearchButtons || props.vehicles.length === 0}
                            data-testid="search-by-vehicles-btn"
                        />
                    </ButtonGroup>
                }
                loading={isCadEventsPollEnabled && isLoadingPoll}
                title={`CAD Events (${cadEventsCount})`}
                testId="RELATED_DATA_CAD_EVENTS"
            >
                {enhancedRelatedData ? (
                    <RelatedDataEntityRows
                        entities={cadEvents}
                        hasMoreRows={hasMoreCadEvents}
                        isLoading={isLoadingViewMore}
                        noEntitiesText="No CAD Events Found"
                        onViewMore={props.onViewMoreCadEvents}
                        RowComponent={CadEventRow}
                    />
                ) : (
                    <CadEventsTable cadEvents={cadEvents} />
                )}
            </TabSection>
            <TabSection
                loading={isRmsReportsPollEnabled && isLoadingPoll}
                title={`RMS History (${rmsReportsCount}) `}
                testId="RELATED_DATA_RMS_HISTORY"
            >
                {enhancedRelatedData ? (
                    <RelatedDataEntityRows
                        entities={rmsReports}
                        hasMoreRows={hasMoreRmsReports}
                        isLoading={isLoadingViewMore}
                        noEntitiesText="No RMS History Found"
                        onViewMore={props.onViewMoreRmsReports}
                        RowComponent={RmsHistoryOrVehicleRow}
                    />
                ) : (
                    <RmsHistoryTable rmsReports={rmsReports} />
                )}
            </TabSection>
            <TabSection
                loading={isVehiclesPollEnabled && isLoadingPoll}
                title={`Vehicles (${vehiclesCount})`}
                testId="RELATED_DATA_VEHICLES"
            >
                {enhancedRelatedData ? (
                    <RelatedDataEntityRows
                        entities={vehicles}
                        hasMoreRows={hasMoreVehicles}
                        isLoading={isLoadingViewMore}
                        noEntitiesText="No Related Vehicles Found"
                        onViewMore={props.onViewMoreVehicles}
                        RowComponent={RmsHistoryOrVehicleRow}
                    />
                ) : (
                    <VehiclesTable vehicles={vehicles} />
                )}
            </TabSection>
        </>
    );
}
